import { Alert, Box, Button, Chip, Divider, Grid, Radio, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { AvailablePaymentMethodsEnum, hasFlag, getPaymentMethodDescription } from 'src/shared/models/enums/availablePaymentMethods.enum';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type OnboardingStepPaymentMethodsProps = {
  onNavigateAway?: () => void;
};

export const OnboardingStepPaymentMethods = ({ onNavigateAway }: OnboardingStepPaymentMethodsProps) => {
  const { t } = useTranslation(['onboarding', 'invoice']);
  const isMounted = useIsMounted();
  const [groups, setGroups] = useState<InvoiceGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllInvoiceGroups(0, 100, 'name', 'asc', '');
      if (isMounted()) setGroups(res.data.results || []);
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allMethods = [
    AvailablePaymentMethodsEnum.Letter,
    AvailablePaymentMethodsEnum.Email,
    AvailablePaymentMethodsEnum.Card,
    AvailablePaymentMethodsEnum.Direct,
    AvailablePaymentMethodsEnum.Swish,
    AvailablePaymentMethodsEnum.Autogiro,
    AvailablePaymentMethodsEnum.FSPay,
    AvailablePaymentMethodsEnum.AutogiroYear,
    AvailablePaymentMethodsEnum.StripeMonthly,
    AvailablePaymentMethodsEnum.ElectronicInvoice,
    AvailablePaymentMethodsEnum.SwishMonthly,
    AvailablePaymentMethodsEnum.SwishYear,
  ];

  const getActiveMethods = (group: InvoiceGroup) => allMethods.filter((method) => hasFlag(group.paymentMethod, method));

  const renderCheckoutPreview = (activeMethods: AvailablePaymentMethodsEnum[], group: InvoiceGroup) => (
    <Box sx={{ mt: 1.5, border: 1, borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden', maxWidth: 360 }}>
      <Box sx={{ bgcolor: 'grey.800', color: '#fff', px: 2, py: 1.5 }}>
        <Typography variant='caption' fontWeight={600}>
          {t('onboarding:stepContent.paymentMethods.previewTitle')}
        </Typography>
      </Box>
      <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: 1, borderColor: 'grey.200' }}>
        <Typography variant='caption' color='text.secondary'>
          {t('onboarding:stepContent.paymentMethods.amountToPay')}
        </Typography>
        <Typography variant='h5' fontWeight={700}>
          250,00 kr
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant='body2' fontWeight={600} sx={{ mb: 1 }}>
          {group.pmSelectionHeader || t('onboarding:stepContent.paymentMethods.selectMethod')}
        </Typography>
        {activeMethods.map((method, idx) => (
          <Box
            key={method}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              px: 1,
              mb: 0.5,
              border: 1,
              borderColor: idx === 0 ? 'primary.main' : 'grey.300',
              borderRadius: 1,
              bgcolor: idx === 0 ? 'primary.50' : '#fff',
            }}
          >
            <Radio checked={idx === 0} size='small' sx={{ p: 0 }} disabled />
            <Typography variant='body2'>{getPaymentMethodDescription(method, t)}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ px: 2, pb: 2 }}>
        <Box
          sx={{
            bgcolor: 'grey.700',
            color: '#fff',
            textAlign: 'center',
            py: 1,
            borderRadius: 1,
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          {t('onboarding:stepContent.paymentMethods.payButton')}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <Typography variant='body1' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.paymentMethods.description')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.paymentMethods.hint')}
            </Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<OpenInNewIcon />}
              href='https://hjalp.foreningssupport.se/category/355-aktivera-betalmetoder'
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('onboarding:readMoreHelp')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1 }}>
            {t('onboarding:stepContent.paymentMethods.groupsTitle')}
          </Typography>
          <Box sx={{ bgcolor: '#fff', borderRadius: 1, p: 2, border: 1, borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              {t('onboarding:stepContent.paymentMethods.editHintPrefix')}{' '}
              <Button
                variant='text'
                size='small'
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  verticalAlign: 'baseline',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                }}
                onClick={() => {
                  if (onNavigateAway) onNavigateAway();
                }}
              >
                {t('onboarding:stepContent.paymentMethods.editHintLink')}
              </Button>
            </Alert>
            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Skeleton variant='rounded' height={48} />
                <Skeleton variant='rounded' height={48} />
              </Box>
            )}
            {!loading && groups.length === 0 && <Alert severity='warning'>{t('onboarding:stepContent.paymentMethods.noGroups')}</Alert>}
            {!loading && groups.length > 0 && (
              <Box>
                {(() => {
                  let previewShown = false;
                  return groups.map((group) => {
                    const activeMethods = getActiveMethods(group);
                    const showPreview = !previewShown && activeMethods.length > 0;
                    if (showPreview) previewShown = true;
                    return (
                      <Box key={group.owner_id} sx={{ mb: 2 }}>
                        <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 0.5 }}>
                          {t('onboarding:stepContent.paymentMethods.groupPrefix')}: {group.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {activeMethods.length > 0 ? (
                            activeMethods.map((method) => (
                              <Chip key={method} label={getPaymentMethodDescription(method, t)} size='small' variant='outlined' />
                            ))
                          ) : (
                            <Typography variant='body2' color='text.secondary'>
                              {t('onboarding:stepContent.paymentMethods.noMethods')}
                            </Typography>
                          )}
                        </Box>
                        {showPreview && renderCheckoutPreview(activeMethods, group)}
                        <Divider sx={{ mt: 1.5 }} />
                      </Box>
                    );
                  });
                })()}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
